<template>
  <div class="h5-register">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>
    
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="logo-area">
        <img src="@/assets/img/logo.png" alt="logo" class="logo-img" />
        <span class="logo-text">{{ systemInfo.name || '渠道注册' }}</span>
      </div>
      <p class="page-desc">合作伙伴注册平台</p>
    </div>

    <!-- 步骤指示器 -->
    <StepIndicator :currentStep="currentStep" :isSmallScreen="isSmallScreen" />

    <!-- 步骤1：选择平台（SaaS化统一包新增） -->
    <PlatformSelectStep
      v-if="currentStep === 1"
      :platforms="platformList"
      :selectedPlatform="selectedPlatform"
      :loading="platformLoading"
      @select="selectPlatform"
      @next="confirmPlatform"
    />

    <!-- 步骤2：手机号验证 -->
    <PhoneVerifyStep
      v-if="currentStep === 2"
      :phone.sync="form.phone"
      :code.sync="form.code"
      :agreeProtocol="agreeProtocol"
      :codeBtnDisabled="codeBtnDisabled"
      :codeBtnText="codeBtnText"
      @toggle-protocol="toggleProtocol"
      @show-agreement="showAgreement"
      @send-code="sendCode"
      @next="verifyCode"
    />

    <!-- 步骤3：身份证上传 -->
    <IdCardStep
      v-if="currentStep === 3"
      :idCardFront="form.idCardFront"
      :idCardBack="form.idCardBack"
      :ocrName.sync="ocrResult.name"
      :ocrIdCard.sync="ocrResult.idCard"
      @file-change="handleIdCardFileChange"
      @error="showToast($event, 'error')"
      @prev="currentStep = 2"
      @next="goToStep4"
    />

    <!-- 步骤4：选择机构和场站 -->
    <SelectAreaStep
      v-if="currentStep === 4"
      :location="location"
      :organizationId.sync="form.organizationId"
      :stationId.sync="form.stationId"
      :organizationList="organizationList"
      :stationList="stationList"
      :submitLoading="submitLoading"
      @relocate="getLocation"
      @update:organizationId="loadStationList"
      @station-change="form.workArea = $event"
      @prev="currentStep = 3"
      @submit="submitRegister"
    />

    <!-- 步骤5：注册成功 -->
    <ResultStep
      v-if="currentStep === 5"
      :name="ocrResult.name"
      :phone="form.phone"
      :orgName="selectedOrgName"
      :workArea="form.workArea"
    />

    <!-- OCR识别加载 -->
    <LoadingOverlay :visible="ocrLoading" message="正在识别身份证..." />

    <!-- Toast提示 -->
    <H5Toast :visible="toast.show" :message="toast.message" :type="toast.type" />

    <!-- 协议弹窗 -->
    <AgreementModal
      :visible="agreementModal.show"
      :type="agreementModal.type"
      @close="closeAgreement"
      @agree="agreeAndClose"
    />
  </div>
</template>

<script>
/**
 * H5渠道自主注册页面（组件化重构版）
 * 作者：孙艺
 * 功能：手机验证码 -> 身份证OCR识别 -> 定位选择机构场站 -> 提交注册
 */
import StepIndicator from './components/StepIndicator.vue'
import PlatformSelectStep from './components/PlatformSelectStep.vue'
import PhoneVerifyStep from './components/PhoneVerifyStep.vue'
import IdCardStep from './components/IdCardStep.vue'
import SelectAreaStep from './components/SelectAreaStep.vue'
import ResultStep from './components/ResultStep.vue'
import LoadingOverlay from './components/LoadingOverlay.vue'
import H5Toast from './components/H5Toast.vue'
import AgreementModal from './components/AgreementModal.vue'

export default {
  name: 'H5ChannelRegister',
  components: {
    StepIndicator,
    PlatformSelectStep,
    PhoneVerifyStep,
    IdCardStep,
    SelectAreaStep,
    ResultStep,
    LoadingOverlay,
    H5Toast,
    AgreementModal
  },
  data() {
    return {
      systemInfo: {
        name: '',
        logo: ''
      },
      // 平台选择相关（SaaS化统一包新增）
      platformList: [],
      selectedPlatform: null,
      platformLoading: false,
      currentStep: 1,
      form: {
        phone: '',
        code: '',
        idCardFront: '',
        idCardBack: '',
        organizationId: '',
        stationId: '',
        workArea: ''
      },
      ocrResult: {
        name: '',
        idCard: ''
      },
      ocrData: null,
      location: {
        loading: false,
        error: '',
        address: '',
        city: '',
        province: '',
        lat: null,
        lng: null
      },
      codeBtnDisabled: false,
      codeBtnText: '获取验证码',
      countdown: 60,
      ocrLoading: false,
      submitLoading: false,
      organizationList: [],
      stationList: [],
      agreeProtocol: false,
      verifyToken: '',
      codeTimer: null,
      toast: {
        show: false,
        message: '',
        type: 'info',
        timer: null
      },
      screenWidth: window.innerWidth,
      agreementModal: {
        show: false,
        type: 'user'
      }
    }
  },
  computed: {
    canSubmitIdCard() {
      return this.form.idCardFront && this.form.idCardBack && this.ocrResult.name && this.ocrResult.idCard
    },
    canSubmitOrg() {
      return this.form.organizationId && this.form.stationId && this.form.workArea
    },
    selectedOrgName() {
      const org = this.organizationList.find(o => o.id === this.form.organizationId)
      return org ? org.name : ''
    },
    isSmallScreen() {
      return this.screenWidth < 380
    }
  },
  created() {
    this.getSystemInfo()
    this.loadPlatformList()
    try {
      this.loadOrganizationList()
    } catch (error) {
      console.error('初始化加载机构列表失败:', error)
    }
  },
  mounted() {
    this.resetViewport()
    setTimeout(() => {
      try {
        this.getLocation()
      } catch (error) {
        console.error('获取定位失败:', error)
        this.location.loading = false
        this.location.error = '定位失败，请手动选择机构'
      }
    }, 100)
  },
  beforeDestroy() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer)
      this.codeTimer = null
    }
    this.restoreViewport()
  },
  methods: {
    // ========== 系统信息 ==========
    async getSystemInfo() {
      try {
        const res = await this.$axios.get('/api/v1/common/title')
        if (res && res.code === 1 && res.data) {
          this.systemInfo = {
            name: res.data.name || res.data.title || '',
            logo: res.data.logo || ''
          }
        }
      } catch (error) {
        console.error('获取系统信息失败:', error)
      }
    },
    
    // ========== Viewport管理 ==========
    resetViewport() {
      let viewport = document.querySelector('meta[name="viewport"]')
      if (viewport) {
        this.originalViewport = viewport.getAttribute('content')
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover')
      }
      document.documentElement.style.fontSize = '16px'
      // 防止页面整体滚动
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.height = '100%'
      document.documentElement.style.overflow = 'hidden'
    },
    restoreViewport() {
      if (this.originalViewport) {
        let viewport = document.querySelector('meta[name="viewport"]')
        if (viewport) {
          viewport.setAttribute('content', this.originalViewport)
        }
      }
      // 恢复页面滚动
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.height = ''
      document.documentElement.style.overflow = ''
    },

    // ========== Toast提示 ==========
    showToast(message, type = 'info', duration = 2500) {
      if (this.toast.timer) {
        clearTimeout(this.toast.timer)
      }
      this.toast.message = message
      this.toast.type = type
      this.toast.show = true
      this.toast.timer = setTimeout(() => {
        this.toast.show = false
        this.toast.timer = null
      }, duration)
    },

    // ========== 平台选择（SaaS化统一包新增） ==========
    async loadPlatformList() {
      this.platformLoading = true
      try {
        // 如果URL中带了appKey参数，说明是从管理端扫码来的，直接跳过平台选择
        const urlAppKey = this.$route.query.appKey
        if (urlAppKey) {
          // 有appKey参数时，设为选中平台并直接跳到步骤2
          this.selectedPlatform = { appKey: urlAppKey }
          this.currentStep = 2
          this.platformLoading = false
          return
        }
        const res = await this.$axios.get('/api/v1/common/platform/list')
        if (res && res.code === 1 && res.data) {
          this.platformList = res.data
          // 如果只有一个平台，自动选中并跳过
          if (this.platformList.length === 1) {
            this.selectedPlatform = this.platformList[0]
            this.currentStep = 2
          }
        }
      } catch (error) {
        console.error('加载平台列表失败:', error)
        this.showToast('加载平台列表失败', 'error')
      } finally {
        this.platformLoading = false
      }
    },
    selectPlatform(platform) {
      this.selectedPlatform = platform
    },
    confirmPlatform() {
      if (!this.selectedPlatform) {
        this.showToast('请选择所属平台', 'error')
        return
      }
      this.currentStep = 2
    },

    // ========== 协议相关 ==========
    toggleProtocol() {
      this.agreeProtocol = !this.agreeProtocol
    },
    showAgreement(type) {
      this.agreementModal.type = type
      this.agreementModal.show = true
    },
    closeAgreement() {
      this.agreementModal.show = false
    },
    agreeAndClose() {
      this.agreeProtocol = true
      this.closeAgreement()
    },

    // ========== 步骤2：手机验证 ==========
    async sendCode() {
      const phoneReg = /^1[3-9]\d{9}$/
      if (!phoneReg.test(this.form.phone)) {
        this.showToast('请输入正确的手机号', 'error')
        return
      }
      try {
        const appKey = this.selectedPlatform ? this.selectedPlatform.appKey : undefined
        const res = await this.$axios.post('/api/v1/h5/channel/sendSmsCode', { phone: this.form.phone, appKey })
        if (res && res.code === 1) {
          this.showToast('验证码已发送', 'success')
          this.codeBtnDisabled = true
          this.countdown = 60
          this.codeBtnText = `${this.countdown}s`
          if (this.codeTimer) clearInterval(this.codeTimer)
          this.codeTimer = setInterval(() => {
            this.countdown--
            this.codeBtnText = `${this.countdown}s`
            if (this.countdown <= 0) {
              clearInterval(this.codeTimer)
              this.codeTimer = null
              this.codeBtnDisabled = false
              this.codeBtnText = '获取验证码'
            }
          }, 1000)
        } else {
          this.showToast(res?.msg || '发送失败，请重试', 'error')
        }
      } catch (error) {
        console.error('发送验证码失败:', error)
        this.showToast('发送失败，请重试', 'error')
      }
    },
    async verifyCode() {
      if (!this.form.code || this.form.code.length < 4) {
        this.showToast('请输入验证码', 'error')
        return
      }
      try {
        const appKey = this.selectedPlatform ? this.selectedPlatform.appKey : undefined
        const res = await this.$axios.post('/api/v1/h5/channel/verifySmsCode', { phone: this.form.phone, code: this.form.code, appKey })
        if (res && res.code === 1) {
          if (res.data && res.data.token) {
            this.verifyToken = res.data.token
          }
          this.currentStep = 3
        } else {
          this.showToast(res?.msg || '验证码错误', 'error')
        }
      } catch (error) {
        console.error('验证验证码失败:', error)
        this.showToast('验证失败，请重试', 'error')
      }
    },

    // ========== 步骤3：身份证上传 ==========
    handleIdCardFileChange({ type, base64 }) {
      if (type === 'front') {
        this.form.idCardFront = base64
        this.performOCR(base64)
      } else {
        this.form.idCardBack = base64
      }
    },
    performOCR(imageBase64) {
      this.ocrLoading = true
      try {
        const formData = new FormData()
        const blob = this.base64ToBlob(imageBase64)
        formData.append('idCardFront', blob, 'idCardFront.jpg')
        if (this.form.idCardBack) {
          const backBlob = this.base64ToBlob(this.form.idCardBack)
          formData.append('idCardBack', backBlob, 'idCardBack.jpg')
        }
        setTimeout(async () => {
          try {
            const res = await this.$axios.post('/api/v1/h5/channel/uploadIdCardAndOcr', formData)
            if (res && res.code === 1 && res.data && res.data.ocrResult) {
              const ocrData = res.data.ocrResult
              this.ocrData = ocrData
              this.ocrResult.name = ocrData.name || ''
              this.ocrResult.idCard = ocrData.idCard || ''
              if (res.data.idCardFront) this.form.idCardFront = res.data.idCardFront
              if (res.data.idCardBack) this.form.idCardBack = res.data.idCardBack
              this.showToast('身份证识别成功，请核对信息', 'success')
            } else {
              this.showToast(res?.msg || '识别失败，请重试', 'error')
            }
          } catch (error) {
            console.error('OCR识别失败:', error)
            this.showToast('识别失败，请重试', 'error')
          } finally {
            this.ocrLoading = false
          }
        }, 1000)
      } catch (error) {
        console.error('OCR识别失败:', error)
        this.showToast('识别失败，请重试', 'error')
        this.ocrLoading = false
      }
    },
    base64ToBlob(base64) {
      const arr = base64.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new Blob([u8arr], { type: mime })
    },
    goToStep4() {
      if (!this.canSubmitIdCard) {
        this.showToast('请完善身份信息', 'error')
        return
      }
      const idCardReg = /^\d{17}[\dXx]$/
      if (!idCardReg.test(this.ocrResult.idCard)) {
        this.showToast('请输入正确的身份证号', 'error')
        return
      }
      this.currentStep = 4
      if (!this.location.address && !this.location.loading) {
        this.getLocation()
      }
    },

    // ========== 步骤4：选择区域 ==========
    getLocation() {
      this.location.loading = true
      this.location.error = ''
      
      // 使用高德地图定位
      if (typeof AMap === 'undefined') {
        this.location.loading = false
        this.location.error = '地图服务加载失败，请刷新页面重试'
        this.loadOrganizationList()
        return
      }
      
      const self = this
      
      // 先尝试精确定位，失败后使用IP定位
      AMap.plugin('AMap.Geolocation', function() {
        const geolocation = new AMap.Geolocation({
          enableHighAccuracy: true,
          timeout: 8000,
          needAddress: true,
          extensions: 'all'
        })
        
        geolocation.getCurrentPosition(function(status, result) {
          if (status === 'complete') {
            self.handleLocationSuccess(result)
          } else {
            // 精确定位失败，尝试IP定位
            console.log('精确定位失败，尝试IP定位...')
            self.getLocationByIP()
          }
        })
      })
    },
    
    // IP定位（备选方案）
    getLocationByIP() {
      const self = this
      
      AMap.plugin('AMap.CitySearch', function() {
        const citySearch = new AMap.CitySearch()
        citySearch.getLocalCity(function(status, result) {
          if (status === 'complete' && result.info === 'OK') {
            // IP定位成功
            self.location.city = result.city
            self.location.province = result.province
            self.location.address = result.province + result.city
            self.location.loading = false
            
            // 根据城市加载机构和场站
            self.loadOrganizationsByCity(result.city)
          } else {
            // IP定位也失败
            self.location.loading = false
            self.location.error = '定位失败，请手动选择'
            self.loadOrganizationList()
          }
        })
      })
    },
    
    // 处理定位成功
    handleLocationSuccess(result) {
      this.location.lat = result.position.lat
      this.location.lng = result.position.lng
      
      // 获取地址信息
      if (result.addressComponent) {
        const addr = result.addressComponent
        this.location.city = addr.city || addr.province // 直辖市时city为空
        this.location.province = addr.province
        this.location.address = `${addr.province || ''}${addr.city || ''}${addr.district || ''}`
      } else if (result.formattedAddress) {
        this.location.address = result.formattedAddress
      }
      
      this.location.loading = false
      
      // 根据城市加载机构和场站
      if (this.location.city) {
        this.loadOrganizationsByCity(this.location.city)
      } else {
        this.loadOrganizationList()
      }
    },
    // 根据城市加载机构列表
    async loadOrganizationsByCity(city) {
      try {
        const res = await this.$axios.get('/api/v1/h5/channel/organizations', { city })
        if (res && res.code === 1 && res.data && res.data.length > 0) {
          this.organizationList = res.data
          // 自动选择第一个机构
          if (!this.form.organizationId && this.organizationList.length > 0) {
            this.form.organizationId = this.organizationList[0].id
            this.loadStationList()
          }
        } else {
          // 该城市没有机构，加载全部机构
          this.loadOrganizationList()
        }
      } catch (error) {
        console.error('根据城市查询机构失败:', error)
        this.loadOrganizationList()
      }
    },
    async loadOrganizationsByLocation(latitude, longitude) {
      try {
        const res = await this.$axios.get('/api/v1/h5/channel/organizations', { latitude, longitude })
        if (res && res.code === 1 && res.data && res.data.length > 0) {
          this.organizationList = res.data
          if (!this.form.organizationId && this.organizationList.length > 0) {
            this.form.organizationId = this.organizationList[0].id
            this.loadStationList()
          }
        } else {
          this.loadOrganizationList()
        }
      } catch (error) {
        console.error('根据定位查询机构失败:', error)
        this.loadOrganizationList()
      }
    },
    async loadOrganizationList() {
      try {
        const res = await this.$axios.get('/api/v1/h5/channel/organizations')
        if (res && res.code === 1 && res.data) {
          this.organizationList = res.data
        }
      } catch (error) {
        console.error('加载机构列表失败:', error)
        this.showToast('加载机构列表失败', 'error')
      }
    },
    async loadStationList() {
      if (!this.form.organizationId) {
        this.stationList = []
        this.form.stationId = ''
        this.form.workArea = ''
        return
      }
      try {
        // 基于机构查询场站（内部会根据机构所在城市查询）
        const res = await this.$axios.get('/api/v1/h5/channel/stations', { organizationId: this.form.organizationId })
        if (res && res.code === 1 && res.data) {
          this.stationList = res.data
          this.form.stationId = ''
          this.form.workArea = ''
        } else {
          this.stationList = []
        }
      } catch (error) {
        console.error('加载场站列表失败:', error)
        this.stationList = []
      }
    },

    // ========== 提交注册 ==========
    submitRegister() {
      if (!this.canSubmitOrg) {
        this.showToast('请完善注册信息', 'error')
        return
      }
      const idCardReg = /^\d{17}[\dXx]$/
      if (!idCardReg.test(this.ocrResult.idCard)) {
        this.showToast('请输入正确的身份证号', 'error')
        return
      }
      this.submitLoading = true
      try {
        const registerData = {
          phone: this.form.phone,
          name: this.ocrResult.name,
          idCard: this.ocrResult.idCard,
          idCardFront: this.form.idCardFront,
          idCardBack: this.form.idCardBack,
          organizationId: this.form.organizationId,
          stationId: this.form.stationId,
          stationName: this.form.workArea,
          workArea: this.form.workArea,
          ocrResult: this.ocrData,
          appKey: this.selectedPlatform ? this.selectedPlatform.appKey : undefined
        }
        setTimeout(async () => {
          try {
            const res = await this.$axios.post('/api/v1/h5/channel/register', registerData)
            this.submitLoading = false
            if (res && res.code === 1) {
              this.currentStep = 5
            } else {
              this.showToast(res?.msg || '注册失败，请重试', 'error')
            }
          } catch (error) {
            console.error('提交注册失败:', error)
            this.submitLoading = false
            this.showToast('注册失败，请重试', 'error')
          }
        }, 1000)
      } catch (error) {
        console.error('提交注册失败:', error)
        this.submitLoading = false
        this.showToast('注册失败，请重试', 'error')
      }
    }
  }
}
</script>

<style scoped>
.h5-register {
  height: 100vh;
  height: 100dvh; /* 动态视口高度，解决移动端地址栏问题 */
  background: linear-gradient(135deg, #2B77FF 0%, #1A5FD9 100%);
  padding: 16px;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  touch-action: pan-x pan-y;
  -webkit-text-size-adjust: 100%;
  padding-bottom: env(safe-area-inset-bottom);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

/* 背景装饰圆 */
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 6s ease-in-out infinite;
}

.circle-1 {
  width: 200px;
  height: 200px;
  top: -50px;
  right: -50px;
  animation-delay: 0s;
}

.circle-2 {
  width: 150px;
  height: 150px;
  bottom: 20%;
  left: -30px;
  animation-delay: 2s;
}

.circle-3 {
  width: 100px;
  height: 100px;
  top: 40%;
  right: 10%;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); opacity: 0.1; }
  50% { transform: translateY(-20px) scale(1.05); opacity: 0.15; }
}

/* 页面头部 */
.page-header {
  text-align: center;
  padding: 20px 0 10px;
  position: relative;
  z-index: 1;
}

.logo-area {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
}

.logo-img {
  width: 40px;
  height: 40px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
}

.logo-text {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0,0,0,0.2);
  letter-spacing: 2px;
}

.page-desc {
  font-size: 13px;
  color: rgba(255,255,255,0.85);
  margin: 0;
  letter-spacing: 1px;
}

@supports (padding-top: env(safe-area-inset-top)) {
  .h5-register {
    padding-top: calc(16px + env(safe-area-inset-top));
  }
}

@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .h5-register {
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }
}

@media screen and (max-width: 480px) {
  .h5-register {
    padding: 12px;
  }
  
  .logo-img {
    width: 36px;
    height: 36px;
  }
  
  .logo-text {
    font-size: 20px;
  }
  
  .page-desc {
    font-size: 12px;
  }
}

@media screen and (max-width: 360px) {
  .h5-register {
    padding: 10px;
  }
  
  .page-header {
    padding: 16px 0 8px;
  }
}

@media screen and (max-height: 500px) and (orientation: landscape) {
  .h5-register {
    padding: 10px 16px;
  }
  
  .page-header {
    padding: 10px 0 5px;
  }
}
</style>
